import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  adminApi,
  type AdminSubscriptionDetail,
} from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { X, Download, Building2, User, CreditCard } from "lucide-react";

interface AdminSubscriptionDetailModalProps {
  id: string;
  onClose: () => void;
  onRefresh: () => void;
}

const subscriptionStatusLabels: Record<string, string> = {
  active: "Aktiv",
  past_due: "Förfallen",
  canceled: "Avbruten",
  incomplete: "Ofullständig",
  trialing: "Provperiod",
  unpaid: "Obetald",
};

const subscriptionStatusStyles: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  past_due: "bg-red-100 text-red-700",
  canceled: "bg-gray-100 text-gray-700",
  incomplete: "bg-yellow-100 text-yellow-700",
  trialing: "bg-blue-100 text-blue-700",
  unpaid: "bg-red-100 text-red-700",
};

export function AdminSubscriptionDetailModal({
  id,
  onClose,
  onRefresh: _onRefresh,
}: AdminSubscriptionDetailModalProps) {
  const [subscriptionData, setSubscriptionData] =
    useState<AdminSubscriptionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getSubscription(id);
      if (response.success && response.data) {
        setSubscriptionData(response.data);
      } else {
        setError(response.error?.message || "Kunde inte ladda data");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadReceipt = useCallback(async () => {
    setIsDownloading(true);
    try {
      const result = await adminApi.downloadSubscriptionReceipt(id);

      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || `kvitto-${id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Kvitto nedladdat");
      } else {
        toast.error(result.error || "Kunde inte ladda ner kvitto");
      }
    } catch {
      toast.error("Ett fel uppstod vid nedladdning");
    } finally {
      setIsDownloading(false);
    }
  }, [id]);

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Prenumerationsdetaljer
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : subscriptionData ? (
            <SubscriptionContent data={subscriptionData} />
          ) : null}
        </div>

        {/* Footer */}
        {!isLoading && !error && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>
              Stäng
            </Button>
            <Button onClick={handleDownloadReceipt} disabled={isDownloading}>
              {isDownloading ? (
                <Spinner size="sm" />
              ) : (
                <Download size={16} />
              )}
              Ladda ner kvitto
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════

function formatAmount(amount: string | number, currency: string = "SEK"): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

// ═══════════════════════════════════════════════════════════════════
// Subscription content
// ═══════════════════════════════════════════════════════════════════

function SubscriptionContent({ data }: { data: AdminSubscriptionDetail }) {
  const isCanceling = data.status === "active" && data.cancelAtPeriodEnd;

  const companyAddress = [data.companyAddress, data.companyPostalCode, data.companyCity]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="space-y-5">
      {/* Company info */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
          <Building2 size={14} className="text-gray-400" />
          Företag
        </h3>
        <div className="bg-gray-50 rounded-lg p-3 space-y-1">
          <p className="text-sm font-medium text-gray-900">
            {data.companyName}
          </p>
          <p className="text-xs text-gray-500">{data.organizationNumber}</p>
          {companyAddress && (
            <p className="text-xs text-gray-500">{companyAddress}</p>
          )}
        </div>
      </div>

      {/* User info */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
          <User size={14} className="text-gray-400" />
          Användare
        </h3>
        <div className="bg-gray-50 rounded-lg p-3 space-y-1">
          {data.userName && (
            <p className="text-sm font-medium text-gray-900">
              {data.userName}
            </p>
          )}
          <p className="text-xs text-gray-500">{data.userEmail || "-"}</p>
        </div>
      </div>

      {/* Subscription details */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
          <CreditCard size={14} className="text-gray-400" />
          Prenumeration
        </h3>
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Status</span>
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                isCanceling
                  ? "bg-yellow-100 text-yellow-700"
                  : subscriptionStatusStyles[data.status] ||
                    "bg-gray-100 text-gray-700"
              }`}
            >
              {isCanceling
                ? "Avslutas vid periodens slut"
                : subscriptionStatusLabels[data.status] || data.status}
            </span>
          </div>
          {data.amount != null && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Belopp</span>
              <span className="text-sm font-medium text-gray-900">
                {parseFloat(data.amount) === 0
                  ? "Gratis"
                  : formatAmount(data.amount, data.currency || "SEK")}
              </span>
            </div>
          )}
          {data.discountAmount && parseFloat(data.discountAmount) > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Rabatt</span>
              <span className="text-xs text-green-600">
                -{formatAmount(data.discountAmount, data.currency || "SEK")}
                {data.campaignCode && (
                  <span className="ml-1 text-gray-400">
                    ({data.campaignCode})
                  </span>
                )}
              </span>
            </div>
          )}
          {data.campaignCode && (!data.discountAmount || parseFloat(data.discountAmount) === 0) && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Kampanjkod</span>
              <span className="text-xs text-gray-700">{data.campaignCode}</span>
            </div>
          )}
          {data.currentPeriodStart && data.currentPeriodEnd && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Nuvarande period</span>
              <span className="text-sm text-gray-900">
                {new Date(data.currentPeriodStart).toLocaleDateString("sv-SE")}{" "}
                –{" "}
                {new Date(data.currentPeriodEnd).toLocaleDateString("sv-SE")}
              </span>
            </div>
          )}
          {data.canceledAt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Avbruten</span>
              <span className="text-sm text-gray-900">
                {new Date(data.canceledAt).toLocaleDateString("sv-SE")}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Skapad</span>
            <span className="text-sm text-gray-900">
              {new Date(data.createdAt).toLocaleString("sv-SE")}
            </span>
          </div>
          {data.updatedAt && data.updatedAt !== data.createdAt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Senast uppdaterad</span>
              <span className="text-sm text-gray-900">
                {new Date(data.updatedAt).toLocaleString("sv-SE")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Stripe IDs */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
        <p className="text-xs text-gray-500">
          <span className="font-medium">ID:</span> {data.id}
        </p>
        <p className="text-xs text-gray-500">
          <span className="font-medium">Stripe Subscription:</span>{" "}
          {data.stripeSubscriptionId}
        </p>
        {data.stripeCustomerId && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">Stripe Customer:</span>{" "}
            {data.stripeCustomerId}
          </p>
        )}
        {data.stripePriceId && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">Stripe Price:</span>{" "}
            {data.stripePriceId}
          </p>
        )}
      </div>
    </div>
  );
}
